/**
 * Accounts Matcher — Instant Funding pair calculator + live dashboard.
 *
 * Replaces the old pre-purchase SKU recommender. Three sections:
 *   1. Calculator — operator inputs two leg configs, sees pair-level
 *      derived numbers (per-trade SL, daily target, days-to-withdrawal,
 *      lifetime SL-hit budget) — pure client-side math.
 *   2. Owned IF Accounts — every account where challenge_type='instant'.
 *      Each card shows static rule caps + per-trade SL derivation.
 *      Live equity/balance fields are stubbed in v1; a follow-up PR
 *      pipes HedgePair DO snapshots into D1.
 *   3. Active IF Pairs — every account_pairs row where ≥1 leg is
 *      challenge_type='instant'. Side-by-side leg comparison with the
 *      binding leg highlighted.
 */

import { useEffect, useState, type ReactNode } from "react";
import { CoolIcon } from "@/components/ui/cool-icon";
import { PageHeader } from "@/components/page-header";
import { DashboardSection } from "@/components/dashboard-section";
import { Loader2 } from "@/components/ui/icons";
import {
  listIfAccounts,
  listIfPairs,
  type IfAccountSummary,
  type IfPairSummary,
} from "@/lib/api/accounts-matcher";
import { extractApiErrorMessage } from "@/lib/api";
import { Calculator } from "@/components/accounts-matcher/calculator";
import { IfAccountCard } from "@/components/accounts-matcher/if-account-card";
import { IfPairCard } from "@/components/accounts-matcher/if-pair-card";

export function AccountsMatcherPage(): ReactNode {
  const [accounts, setAccounts] = useState<IfAccountSummary[] | null>(null);
  const [pairs, setPairs] = useState<IfPairSummary[] | null>(null);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const [pairsError, setPairsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listIfAccounts()
      .then((res) => {
        if (cancelled) return;
        setAccounts(res.accounts);
      })
      .catch((e) => {
        if (cancelled) return;
        setAccountsError(extractApiErrorMessage(e));
      });
    listIfPairs()
      .then((res) => {
        if (cancelled) return;
        setPairs(res.pairs);
      })
      .catch((e) => {
        if (cancelled) return;
        setPairsError(extractApiErrorMessage(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Accounts Matcher"
        accentWords={["Matcher"]}
        icon={<CoolIcon name="Calculator" size={20} />}
        subtitle="Plan IF Pairs Without Burning A 1%."
      />

      <DashboardSection title="Pair Calculator">
        <Calculator />
      </DashboardSection>

      <DashboardSection title="My IF Accounts">
        <AccountsList accounts={accounts} error={accountsError} />
      </DashboardSection>

      <DashboardSection title="Active IF Pairs">
        <PairsList pairs={pairs} error={pairsError} />
      </DashboardSection>
    </div>
  );
}

function AccountsList({
  accounts,
  error,
}: {
  accounts: IfAccountSummary[] | null;
  error: string | null;
}): ReactNode {
  if (error) {
    return <p className="text-sm text-destructive">{error}</p>;
  }
  if (accounts === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" /> Loading…
      </div>
    );
  }
  if (accounts.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border/60 bg-muted/10 px-4 py-8 text-center text-sm text-muted-foreground">
        No Instant-Funding Accounts Yet. Add One From The Accounts Page To
        See It Here.
      </div>
    );
  }
  return (
    <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {accounts.map((a, i) => (
        <li key={a.id}>
          <IfAccountCard account={a} index={i} />
        </li>
      ))}
    </ul>
  );
}

function PairsList({
  pairs,
  error,
}: {
  pairs: IfPairSummary[] | null;
  error: string | null;
}): ReactNode {
  if (error) {
    return <p className="text-sm text-destructive">{error}</p>;
  }
  if (pairs === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" /> Loading…
      </div>
    );
  }
  if (pairs.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border/60 bg-muted/10 px-4 py-8 text-center text-sm text-muted-foreground">
        No Active IF Pairs. Pair An Instant-Funding Account With A
        Counterparty From The Pairs Page.
      </div>
    );
  }
  return (
    <ul className="flex flex-col gap-3">
      {pairs.map((p, i) => (
        <li key={p.pairSlug}>
          <IfPairCard pair={p} index={i} />
        </li>
      ))}
    </ul>
  );
}
